/** The new design's shared building blocks (kit boards: docs/design/canvas/project/Kit*.dc.html).
 *  Every redone page uses these, so cards, states, tags, fields and dialogs look and behave the same.
 *  Styles: ./kit.css (scoped under the page root class "nh"). Lifted from the new Home unchanged. */
import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import {
  ArrowDownRight, ArrowRight, ArrowUpRight, CircleAlert, CircleCheck, CircleX, FilePenLine, PackageCheck, RotateCcw, Send,
  Truck, X, type LucideIcon,
} from "lucide-react";

import type { DocStatus, DocSummary } from "../../lib/api";
import { day } from "../../lib/format";
import "./kit.css";

export function Ic({ i: I, s = 18, cls }: { i: LucideIcon; s?: number; cls?: string }) {
  return <I size={s} className={cls} aria-hidden="true" />;
}

export function signed(change: number, dp = 1) {
  return `${change >= 0 ? "+" : "−"}${Math.abs(change * 100).toFixed(dp)}%`;
}

export function Delta({ change }: { change: number | null }) {
  if (change == null) return null;
  const flat = Math.abs(change) < 0.01;
  const up = change > 0;
  const I = flat ? ArrowRight : up ? ArrowUpRight : ArrowDownRight;
  return <span className={`nh-delta ${flat ? "tx-neu" : up ? "tx-good" : "tx-crit"}`} data-testid="delta"><Ic i={I} s={13} />{signed(change)}</span>;
}

export function Card({ area, icon, title, right, isNew, children, testId }: {
  area?: string; icon: LucideIcon; title: ReactNode; right?: ReactNode; isNew?: boolean; children: ReactNode; testId?: string;
}) {
  return (
    <section className={`nh-card nh-cq${area ? ` nh-a-${area}` : ""}`} aria-label={typeof title === "string" ? title : undefined} data-testid={testId}>
      <div className="nh-card-h">
        <h2><Ic i={icon} />{title}{isNew ? <span className="nh-new">NEW</span> : null}</h2>
        {right ? <span className="r">{right}</span> : null}
      </div>
      {children}
    </section>
  );
}

export function Skel({ n = 4 }: { n?: number }) {
  const w = [92, 70, 84, 60, 78, 66, 88, 74];
  return <div className="nh-skrows" aria-busy="true" aria-label="Loading">{Array.from({ length: n }, (_, i) => <span key={i} className="nh-sk" style={{ width: `${w[i % w.length]}%` }} />)}</div>;
}

export function Failed({ what, error, retry }: { what: string; error: string; retry: () => void }) {
  return (
    <div className="nh-err" role="alert">
      <Ic i={CircleX} />
      <div>
        <b>Could not load {what}</b>
        <p>{error}. Nothing was changed; the rest of the page is fine.</p>
        <button type="button" className="nh-btn sec sm" onClick={retry}><Ic i={RotateCcw} s={15} />Try again</button>
      </div>
    </div>
  );
}

export function Clear({ icon = CircleCheck, good = true, title, children }: { icon?: LucideIcon; good?: boolean; title: string; children: ReactNode }) {
  return (
    <div className="nh-clear">
      <span className={`nh-circ${good ? " ico-good" : ""}`}><Ic i={icon} s={22} /></span>
      <div><b>{title}</b><p>{children}</p></div>
    </div>
  );
}

export const DOC_STATUS: Record<DocStatus, { tone: string; icon: LucideIcon; word: string }> = {
  draft: { tone: "neu", icon: FilePenLine, word: "Draft" }, issued: { tone: "info", icon: Send, word: "Issued" },
  shipped: { tone: "warn", icon: Truck, word: "Shipped" }, received: { tone: "good", icon: PackageCheck, word: "Received" },
  cancelled: { tone: "neu", icon: X, word: "Cancelled" },
};
export function DocTag({ d }: { d: DocSummary }) {
  const s = DOC_STATUS[d.status];
  const word = d.status === "shipped" && d.doc_type === "transfer_invoice" ? "In transit" : s.word;
  return <span className={`nh-tag ${s.tone}`} data-status={d.status}><Ic i={s.icon} s={13} />{word}</span>;
}

export const dayShort = (iso: string | null | undefined) => (iso ? day(iso).replace(/ \d{4}$/, "") : "—");

export function useDialogBasics(onClose: () => void) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.querySelector<HTMLElement>("input, select")?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("keydown", esc); prev?.focus?.(); };
  }, []);
  return box;
}

export function Problems({ list, onPick }: { list: { field: string; text: string }[]; onPick: (f: string) => void }) {
  if (!list.length) return null;
  return (
    <div className="nh-banner crit" role="alert" data-testid="problems">
      <Ic i={CircleX} />
      <div><b>{list.length} thing{list.length === 1 ? "" : "s"} to fix before saving</b>
        {list.map((p) => <div key={p.field}><button type="button" className="linkish" onClick={() => onPick(p.field)}>{p.text}</button></div>)}</div>
    </div>
  );
}

export function Field({ id, label, help, error, children }: { id: string; label: string; help?: ReactNode; error?: string; children: ReactNode }) {
  return (
    <div className="nh-fld">
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? <span className="nh-emsg" id={`${id}-err`}><Ic i={CircleAlert} s={13} />{error}</span> : help ? <p className="nh-help" id={`${id}-help`}>{help}</p> : null}
    </div>
  );
}

export function Dialog({ title, onClose, foot, children, boxRef }: { title: string; onClose: () => void; foot: ReactNode; children: ReactNode; boxRef: RefObject<HTMLDivElement> }) {
  return (
    <div className="nh-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nh-dlg" role="dialog" aria-modal="true" aria-label={title} ref={boxRef}>
        <div className="nh-dlg-h"><h2>{title}</h2><button type="button" className="nh-btn qt io" aria-label="Close" onClick={onClose}><Ic i={X} s={15} /></button></div>
        <div className="nh-dlg-b">{children}</div>
        <div className="nh-dlg-f">{foot}</div>
      </div>
    </div>
  );
}

